import { Platform } from "react-native";

/**
 * Whether this platform should read a pack barcode by photographing it.
 *
 * Reading an EAN-13 or a GS1 DataMatrix is a decode that happens wherever the
 * image is. Native does it live through `expo-camera`, which handles every
 * symbology a pharmacy pack carries. The web cannot: `expo-camera`'s web build
 * decodes QR only, and the browser API that would cover the rest,
 * `BarcodeDetector`, is absent from Safari — so iOS, which has to work, has no
 * on-device decoder at all.
 *
 * There, she photographs the box and the server reads it. On a desktop browser
 * that would be a file picker standing in for a camera, which is not a scanner
 * and does not pretend to be one, so desktop keeps typing the printed code.
 *
 * The test is the pointer, not the user agent and not whether a camera exists.
 * A laptop has a webcam and would pass a camera check, but `capture` is only a
 * hint and desktop browsers ignore it — the control there opens a file picker,
 * never the webcam. So the question worth asking is "is this a device held in
 * the hand", which `pointer: coarse` answers, and which is already how this
 * app tells mobile web from desktop web (`MoonTimeField`, `useInputFontSize`).
 */
export function canPhotographPackCode(): boolean {
  if (Platform.OS !== "web") return false;
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** The longest edge we send; a pack barcode is legible well below this. */
const MAX_PACK_PHOTO_EDGE = 1600;
const PACK_PHOTO_QUALITY = 0.8;

/**
 * Turn whatever the camera produced into something the decoder accepts.
 *
 * Two problems, one answer. An iPhone photographs in HEIC, which the server
 * cannot decode and rejects as an unsupported type; and a modern phone camera
 * produces files large enough to be refused for size. Drawing the picture to a
 * canvas and exporting it re-encodes it as JPEG and shrinks it in one pass —
 * and the browser doing the decoding is the one that understands HEIC, which
 * is the only reason this works on the device that needs it.
 *
 * Web only. Native never reaches here: `expo-camera` decodes the barcode on
 * the device and no image is sent at all.
 */
export async function preparePackPhoto(
  uri: string,
  fallbackName: string,
): Promise<{ blob: Blob; fileName: string }> {
  const source = await fetch(uri).then((response) => response.blob());

  if (
    typeof document === "undefined" ||
    typeof createImageBitmap !== "function"
  ) {
    return { blob: source, fileName: fallbackName };
  }

  try {
    const bitmap = await createImageBitmap(source);
    const scale = Math.min(
      1,
      MAX_PACK_PHOTO_EDGE / Math.max(bitmap.width, bitmap.height),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) return { blob: source, fileName: fallbackName };
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", PACK_PHOTO_QUALITY),
    );
    // A re-encode that fails is not worth failing the scan over: send what the
    // camera gave us and let the server say whether it can read it.
    if (!encoded) return { blob: source, fileName: fallbackName };
    return { blob: encoded, fileName: "pack.jpg" };
  } catch {
    return { blob: source, fileName: fallbackName };
  }
}
